import { shallowEqual, type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import context from '@alltrails/context/slices/contextSlice';
import currencyCode from '@alltrails/context/slices/currencyCodeSlice';
import plans from '@alltrails/context/slices/plansSlice';
import type Context from '@alltrails/core/types/Context';
import features from '@alltrails/features/slices/features';
import amplitudeAnalyticsSettings from '@alltrails/amplitude/slices/amplitudeAnalyticsSettings';
import algoliaConfigs from '@alltrails/redux/slices/algoliaConfigs';
import { configureStore } from '@reduxjs/toolkit';
import { experimentsReducer as experiments } from '@alltrails/experiments';
import { baseApi } from '@alltrails/redux-helpers';
import { AlgoliaConfigs } from '@alltrails/types/algoliaConfigs';
import type CountryCode from '@alltrails/types/CountryCode';
import type RequiredPreloadedState from '@alltrails/core/types/RequiredPreloadedState';
import lists, { type ListsState } from '@alltrails/lists/slices/lists';
import greetings, { type GreetingsState } from '../slices/greetings';
import sections, { type SectionsState } from '../slices/sections';
import { listenerMiddleware } from './listenerMiddleware';

export type PreloadedState = RequiredPreloadedState & {
  algoliaConfigs: AlgoliaConfigs;
  lists?: ListsState;
  greetings?: GreetingsState;
  sections?: SectionsState;
};

export const reducer = {
  algoliaConfigs,
  amplitudeAnalyticsSettings,
  [baseApi.reducerPath]: baseApi.reducer,
  browser: (state = {}) => state,
  context,
  currencyCode,
  experiments,
  features,
  greetings,
  irclickid: (state = '') => state,
  lists,
  plans,
  requestCountryCode: (state = '' as CountryCode) => state,
  sections
};

export const buildStore = (preloadedState?: PreloadedState) =>
  configureStore({
    reducer,
    preloadedState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(baseApi.middleware, listenerMiddleware.middleware)
  });

export type AppStore = ReturnType<typeof buildStore>;
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<AppStore['getState']> & { context?: Context };

// these are intended to use within the app only
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = (selector: (state: RootState) => any) =>
  useSelector<RootState, any>(selector, shallowEqual);
